import { useTranslation } from "react-i18next";
import { IconInfoCircle, IconLoader2, IconSparkles } from "@tabler/icons-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatMoney, formatDate } from "../lib/format";
import type { MySubscription } from "../api";
import { SubscriptionStatusBadge } from "./SubscriptionStatusBadge";
import { PaymentMethodLabel } from "./PaymentMethodLabel";

interface Props {
  data: MySubscription | undefined;
  isLoading: boolean;
}

export function SubscriptionStatusCard({ data, isLoading }: Props) {
  const { t } = useTranslation(undefined, { keyPrefix: "subscription" });

  if (isLoading || !data) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12 text-ink-soft">
          <IconLoader2 className="animate-spin" size={20} aria-hidden />
        </CardContent>
      </Card>
    );
  }

  if (data.status === "none") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t("statusCard.title")}</CardTitle>
          <p className="text-[12.5px] text-ink-muted">{t("statusCard.subtitle")}</p>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-2 rounded-xl border border-info-soft bg-info-soft/40 px-3 py-2.5">
            <IconSparkles size={16} className="mt-0.5 shrink-0 text-info-ink" aria-hidden />
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-info-ink">{t("statusCard.none.title")}</p>
              <p className="text-[12.5px] text-ink-muted">{t("statusCard.none.description")}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const priceFormatted = formatMoney(data.plan.monthlyPriceCents / 100);
  const cyclePerLabel = t(`billingCycle.${data.plan.billingCycle}`);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>{t("statusCard.title")}</CardTitle>
            <p className="text-[12.5px] text-ink-muted">{t("statusCard.subtitle")}</p>
          </div>
          <SubscriptionStatusBadge status={data.status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="text-[15px] font-semibold text-ink">
            {t("statusCard.planPrefix")} {data.plan.name}
          </span>
          <span className="text-[13px] text-ink-muted">
            · {priceFormatted}/{cyclePerLabel}
          </span>
        </div>

        {data.origin !== "paid" && data.grantedBy && (
          <div className="flex items-start gap-2 rounded-xl border border-info-soft bg-info-soft/40 px-3 py-2.5">
            <IconSparkles size={16} className="mt-0.5 shrink-0 text-info-ink" aria-hidden />
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-info-ink">
                {t(`origin.${data.origin}.title`)}
              </p>
              {data.origin === "free_trial" && data.trialEndsAt && (
                <p className="text-[12.5px] text-ink-muted">
                  {t("origin.free_trial.endsAt", { date: formatDate(data.trialEndsAt) })}
                </p>
              )}
              <p className="text-[12px] text-ink-soft" title={data.grantedBy.reason}>
                {t(`origin.${data.origin}.description`)}
              </p>
            </div>
          </div>
        )}

        {data.nextInvoice && (
          <div className="rounded-xl border border-border bg-bg/40 px-3 py-2.5">
            <p className="text-[11.5px] font-semibold uppercase tracking-wide text-ink-soft">
              {t("statusCard.nextInvoice")}
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-[14px] font-semibold text-ink">
                {formatMoney(data.nextInvoice.amountCents / 100)}
              </span>
              <span className="text-[12.5px] text-ink-muted">
                · {t("statusCard.dueOn", { date: formatDate(data.nextInvoice.dueDate) })}
              </span>
              {data.nextInvoice.method && (
                <>
                  <span className="text-ink-soft">·</span>
                  <PaymentMethodLabel method={data.nextInvoice.method} />
                </>
              )}
            </div>
          </div>
        )}

        {data.defaultPaymentMethod?.kind && data.origin === "paid" && (
          <p className="flex items-center gap-1.5 text-[12.5px] text-ink-muted">
            <IconInfoCircle size={14} aria-hidden />
            <span>
              {t("statusCard.defaultMethod")}{" "}
              <PaymentMethodLabel method={data.defaultPaymentMethod.kind} withLabel />
              {data.defaultPaymentMethod.cardLast4 && (
                <span className="ml-1 font-mono text-[12px] text-ink">
                  •••• {data.defaultPaymentMethod.cardLast4}
                </span>
              )}
            </span>
          </p>
        )}
      </CardContent>
    </Card>
  );
}
